import { Link, useLocation, useMatch, useResolvedPath } from 'react-router-dom';
import { NavItem as UINavItem, Tooltip, cn } from '@playerbase/ui';
import type { ReactNode } from 'react';

type SidebarNavButtonProps = {
  to: string;
  icon: ReactNode;
  label: string;
  collapsed?: boolean;
  showStatusDot?: boolean;
  pulseDot?: boolean;
  end?: boolean;
  // Extra pathname prefixes that should also light the item up. Used
  // for primary entries whose secondary rail covers additional URL
  // roots - e.g. Insights matches /insights and /feedback-settings/insights.
  matchAlso?: string[];
};

// Single sidebar leaf that switches presentation based on `collapsed`.
//
// Expanded: full row via the design system's NavItem - same shape as
// before the shell refactor.
//
// Collapsed: a 40x40 icon-only button with a Tooltip flyout carrying the
// label. The unread pulse moves from the row trailing edge to a small
// glow dot anchored at the bottom-right of the icon so it stays visible
// even when there's no label.
export function SidebarNavButton({
  to,
  icon,
  label,
  collapsed = false,
  showStatusDot = false,
  pulseDot = false,
  end = false,
  matchAlso,
}: SidebarNavButtonProps): JSX.Element {
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end });
  const location = useLocation();
  const matchesAlso =
    matchAlso !== undefined &&
    matchAlso.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );
  const isActive = match !== null || matchesAlso;

  if (!collapsed) {
    return (
      <UINavItem
        as={Link}
        to={to}
        icon={icon}
        label={label}
        showStatusDot={showStatusDot}
        pulseDot={pulseDot}
        isActive={isActive}
      />
    );
  }

  const dotKind: 'pulse' | 'static' | 'none' = pulseDot
    ? 'pulse'
    : showStatusDot
      ? 'static'
      : 'none';

  return (
    <Tooltip content={label} side="right" delayMs={120}>
      <Link
        to={to}
        aria-label={label}
        className={cn(
          'relative flex h-10 w-10 items-center justify-center rounded-md',
          'border-l-2 border-transparent transition-colors duration-150',
          isActive
            ? 'border-l-accent bg-accent-bg text-accent'
            : 'text-text-muted hover:bg-surface-2 hover:text-text',
        )}
      >
        <span className="text-current">{icon}</span>
        {dotKind !== 'none' && (
          <span
            aria-hidden
            className={cn(
              'absolute bottom-1 right-1 h-1.5 w-1.5 rounded-full bg-accent',
              dotKind === 'pulse' && 'motion-safe:animate-glow',
            )}
          />
        )}
      </Link>
    </Tooltip>
  );
}
